import React, { useState, useEffect, useCallback } from 'react';
import { Button } from '../ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../ui/dialog';
import { PlusCircle, Download, Trash2, AlertTriangle, Loader2, Wand2, Sparkles } from 'lucide-react';
import { toast } from '../ui/use-toast';
import { CreateDialog } from './CreateDialog';
import { IdeogramPrompt } from './types';
import { getPrompts, deletePrompt } from '../../services/ideogramService';
import { RemixDialog } from './RemixDialog';

const GeneratingImage = () => (
  <div className="absolute inset-0 bg-gray-100 flex items-center justify-center aspect-video">
    <div className="flex flex-col items-center gap-2">
      <Loader2 className="w-6 h-6 animate-spin" />
      <p className="text-sm text-gray-600">Generating image...</p>
    </div>
  </div>
);

export const Gallery: React.FC = () => {
  const [prompts, setPrompts] = useState<any[]>([]);
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [selectedImage, setSelectedImage] = useState<IdeogramPrompt | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isRemixDialogOpen, setIsRemixDialogOpen] = useState(false);

  const fetchPrompts = useCallback(async (isInitialLoad = false) => {
    if (isInitialLoad) {
      setIsLoading(true);
    }
    try {
      const data = await getPrompts();
      setPrompts(currentPrompts => {
        // Only update if the data has actually changed
        if (JSON.stringify(data.prompts) !== JSON.stringify(currentPrompts)) {
          console.log('Fetched prompts:', data.prompts.length);
          return data.prompts;
        }
        return currentPrompts;
      });
    } catch (error) {
      console.error('Error fetching prompts:', error);
    } finally {
      if (isInitialLoad) {
        setIsLoading(false);
      }
    }
  }, []);

  // Set up polling with memoized callback
  useEffect(() => {
    // Initial fetch
    fetchPrompts(true);
    
    // Set up polling interval
    const interval = setInterval(() => {
      fetchPrompts(false);
    }, 5000);
    
    // Cleanup
    return () => clearInterval(interval);
  }, [fetchPrompts]); // Include fetchPrompts since it's stable due to useCallback

  const startGenerating = useCallback(() => {
    console.log('Starting generation mode');
    // Trigger an immediate fetch when generation starts
    fetchPrompts(false);
  }, [fetchPrompts]);

  const handleDownload = (imageUrl: string) => {
    window.open(imageUrl, '_blank');
  };

  const handleDelete = async (promptId: string) => {
    if (!promptId) return;
    
    try {
      setIsDeleting(true);
      await deletePrompt(promptId);
      
      // Close dialogs and refresh the gallery
      setIsDeleteDialogOpen(false);
      setSelectedImage(null);
      await fetchPrompts();
      
      toast({
        title: "Success",
        description: "Image deleted successfully.",
      });
    } catch (error) {
      console.error('Error deleting image:', error);
      toast({
        title: "Error",
        description: "Failed to delete image. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="flex justify-between items-center mb-8">
        <h1 className="text-2xl font-bold">Ideogram Gallery</h1>
        <Button onClick={() => setIsCreateDialogOpen(true)}>
          <PlusCircle className="mr-2 h-4 w-4" />
          Create
        </Button>
      </div>

      {isLoading ? (
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {prompts && prompts.map((prompt) => (
            <div
              key={prompt.id}
              className="relative group cursor-pointer rounded-lg overflow-hidden aspect-video"
              onClick={() => setSelectedImage(prompt)}
            >
              <img
                src={prompt.imageUrl}
                alt={prompt.prompt}
                className="w-full h-full object-cover rounded-lg"
              />
              {/* Hover overlay */}
              <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity duration-200 flex items-end p-4">
                <p className="text-white text-sm line-clamp-3">{prompt.prompt}</p>
              </div>
              {prompt.status === 'generating' ? (
                <GeneratingImage />
              ) : (
                <>
                  {prompt.isRemix && (
                    <div className="absolute top-2 right-2 bg-primary/80 text-white px-2 py-1 rounded-full text-xs flex items-center">
                      <Sparkles className="w-3 h-3 mr-1" />
                      Remixed
                    </div>
                  )}
                </>
              )}
            </div>
          ))}
        </div>
      )}

      <CreateDialog
        open={isCreateDialogOpen}
        onOpenChange={setIsCreateDialogOpen}
        onStartGenerating={startGenerating}
        initialPrompt={selectedImage?.prompt}
      />

      {/* Image Details Dialog */}
      <Dialog open={!!selectedImage && !isDeleteDialogOpen} onOpenChange={() => setSelectedImage(null)}>
        <DialogContent className="max-w-3xl h-[90vh] flex flex-col">
          <DialogHeader>
            <div className="flex items-center justify-between mr-8">
              <DialogTitle>Image Details</DialogTitle>
              {selectedImage?.isRemix && (
                <span className="bg-primary/80 text-white px-2 py-1 rounded-full text-xs flex items-center">
                  <Sparkles className="w-3 h-3 mr-1" />
                  Remixed
                </span>
              )}
            </div>
          </DialogHeader>
          <div className="flex-1 flex flex-col min-h-0">
            {/* Image container - will shrink to prevent overflow */}
            <div className="flex-1 min-h-0 flex items-center justify-center aspect-video">
              <img
                src={selectedImage?.imageUrl}
                alt={selectedImage?.prompt}
                className="max-w-full max-h-full object-contain rounded-lg"
              />
            </div>
            {/* Info section - will not shrink */}
            <div className="space-y-4 flex-shrink-0 mt-4">
              {/* Prompt Info */}
              <div className="space-y-2">
                <h3 className="font-semibold">Prompt</h3>
                <p className="text-sm text-gray-600">{selectedImage?.prompt}</p>
              </div>
              {/* Style and Aspect Ratio */}
              <div className="flex gap-4">
                <div>
                  <h3 className="font-semibold text-sm">Style</h3>
                  <p className="text-sm text-gray-600">{selectedImage?.style}</p>
                </div>
                <div>
                  <h3 className="font-semibold text-sm">Aspect Ratio</h3>
                  <p className="text-sm text-gray-600">
                    {selectedImage?.aspectRatio.replace('ASPECT_', '').replace('_', ':')}
                  </p>
                </div>
              </div>
              {/* Actions */}
              <div className="flex justify-end gap-2">
                <Button
                  variant="outline"
                  onClick={() => {
                    setIsCreateDialogOpen(true);
                  }}
                >
                  <PlusCircle className="mr-2 h-4 w-4" />
                  Use Prompt
                </Button>
                <Button
                  variant="outline"
                  onClick={() => handleDownload(selectedImage?.imageUrl || '')}
                >
                  <Download className="mr-2 h-4 w-4" />
                  Download
                </Button>
                <Button
                  variant="outline"
                  onClick={() => {
                    setIsRemixDialogOpen(true);
                  }}
                >
                  <Wand2 className="mr-2 h-4 w-4" />
                  Remix
                </Button>
                <Button
                  variant="destructive"
                  onClick={() => {
                    setIsDeleteDialogOpen(true);
                  }}
                >
                  <Trash2 className="mr-2 h-4 w-4" />
                  Delete
                </Button>
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Remix Dialog */}
      {selectedImage && (
        <RemixDialog
          open={isRemixDialogOpen}
          onOpenChange={(open) => {
            setIsRemixDialogOpen(open);
            if (!open) {
              setSelectedImage(null);
            }
          }}
          originalImage={selectedImage!}
          onStartGenerating={startGenerating}
          onCloseImageDetails={() => {}}
        />
      )}

      {/* Delete Confirmation Dialog */}
      <Dialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center">
              <AlertTriangle className="h-5 w-5 text-red-500 mr-2" />
              Confirm Deletion
            </DialogTitle>
            <DialogDescription>
              Are you sure you want to delete this image? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsDeleteDialogOpen(false)}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={() => selectedImage?.id && handleDelete(selectedImage.id)}
              disabled={isDeleting}
            >
              {isDeleting ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  Deleting...
                </>
              ) : (
                'Delete'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
